import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import AxiosInstance from "@/api/axios";
import { getClientId } from "@/utils/helper";

export type DocumentationType = "STATIC" | "DYNAMIC";

export interface IDocumentation {
    _id: string;
    name: string;
    mimeType: string;
    type: DocumentationType;
    path?: string;             // present for STATIC only (resolved URL)
    createdAt: string | null;  // null for DYNAMIC (no real upload date)
}

interface DocumentationState {
    loading: boolean;
    documents: IDocumentation[];
    error: string | null;
}

const initialState: DocumentationState = {
    loading: false,
    documents: [],
    error: null,
};

export const fetchDocuments = createAsyncThunk(
    "documentation/fetchDocuments",
    async () => {
        const clientId = getClientId();
        const response = await AxiosInstance.get("/documentation", {
            params: { clientId },
        });
        return response.data.data as IDocumentation[];
    },
);

export const deleteDocument = createAsyncThunk(
    "documentation/deleteDocument",
    async ({ id }: { id: string }) => {
        await AxiosInstance.delete(`/documentation/${id}`);
        return id;
    },
);

const documentationSlice = createSlice({
    name: "documentation",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchDocuments.fulfilled,
                (state, action: PayloadAction<IDocumentation[]>) => {
                    state.loading = false;
                    state.documents = action.payload;
                },
            )
            .addCase(fetchDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || "Failed to fetch documents.";
            })
            .addCase(
                deleteDocument.fulfilled,
                (state, action: PayloadAction<string>) => {
                    state.documents = state.documents.filter(
                        (doc) => doc._id !== action.payload,
                    );
                },
            );
    },
});

export default documentationSlice.reducer;